/**
 * Transform orientation — computes the gizmo's visual rotation quaternion
 * and maps handle axis grabs to command buffer constraints.
 */

import * as THREE from "three/webgpu";
import type {
  AxisConstraint,
  TransformOrientation,
} from "../../app/storeTypes.ts";

// Scratch objects to avoid per-frame allocations
const _v = new THREE.Vector3();
const _q = new THREE.Quaternion();
const _m = new THREE.Matrix4();

// ---------------------------------------------------------------------------
// Orientation quaternion
// ---------------------------------------------------------------------------

/**
 * Compute the quaternion that orients the gizmo's axes for the given
 * transform orientation.
 *
 * @param orientation  The active transform orientation.
 * @param objectQuat   The selected object's world rotation quaternion (for Local).
 * @param normalDir    The selection normal in object-local space (for Normal).
 *                     Falls back to Local if null.
 * @param camera       The active camera (for View).
 */
export function computeOrientationQuat(
  orientation: TransformOrientation,
  objectQuat: THREE.Quaternion,
  normalDir: THREE.Vector3 | null,
  camera: THREE.Camera,
): THREE.Quaternion {
  switch (orientation) {
    case "global":
      return _q.identity().clone();

    case "local":
      return objectQuat.clone();

    case "normal": {
      if (!normalDir || normalDir.lengthSq() < 1e-10) {
        // Fall back to Local when no valid normal
        return objectQuat.clone();
      }
      // Build a tangent frame from the normal:
      // Z = normal direction, X/Y = arbitrary tangent/bitangent.
      // The normal is in object-local space, so we compose with objectQuat
      // to get world-space orientation.
      const n = _v.copy(normalDir).normalize();

      // Pick an up reference that isn't parallel to the normal
      const up =
        Math.abs(n.y) < 0.99
          ? new THREE.Vector3(0, 1, 0)
          : new THREE.Vector3(1, 0, 0);

      const tangent = new THREE.Vector3().crossVectors(up, n).normalize();
      const bitangent = new THREE.Vector3().crossVectors(n, tangent);

      // Build rotation matrix: columns = tangent (X), bitangent (Y), normal (Z)
      _m.makeBasis(tangent, bitangent, n);
      const localQuat = new THREE.Quaternion().setFromRotationMatrix(_m);

      // Compose with object rotation to get world-space orientation
      return objectQuat.clone().multiply(localQuat);
    }

    case "view": {
      // Align gizmo axes with camera: X = right, Y = up, Z = view direction
      return camera.quaternion.clone();
    }

    default:
      return _q.identity().clone();
  }
}

// ---------------------------------------------------------------------------
// Axis-to-constraint mapping
// ---------------------------------------------------------------------------

/**
 * Map a gizmo handle axis grab to the correct AxisConstraint and optional
 * normalVector for the command buffer.
 *
 * For Global orientation, the handle axis maps directly to the world axis.
 * For all other orientations, the handle's direction is computed from the
 * orientation quaternion and passed as a "normal" constraint with an
 * explicit direction vector — the command buffer's normal-axis projection
 * handles arbitrary vectors.
 *
 * @param handleAxis  Which handle was grabbed ("x", "y", "z", or "free").
 * @param orientation The active transform orientation.
 * @param orientationQuat The gizmo's current orientation quaternion (world-space).
 * @param objectQuat  The edited object's rotation quaternion (needed because
 *                    the command buffer's normal-axis math operates in
 *                    object-local space for edit-mode transforms).
 * @param isEditMode  Whether we're in edit mode (vertex transforms).
 */
export function mapHandleToConstraint(
  handleAxis: string,
  orientation: TransformOrientation,
  orientationQuat: THREE.Quaternion,
  objectQuat: THREE.Quaternion,
  isEditMode: boolean,
): { axis: AxisConstraint; normalVector?: [number, number, number] } {
  // Free / center grab → no constraint
  if (handleAxis === "free") {
    return { axis: null };
  }

  // Global orientation in object mode → direct axis mapping
  if (orientation === "global" && !isEditMode) {
    return { axis: handleAxis as AxisConstraint };
  }

  // Compute the world-space direction of the grabbed axis,
  // then express it in the coordinate space the command buffer expects.
  const unitVec = new THREE.Vector3(
    handleAxis === "x" ? 1 : 0,
    handleAxis === "y" ? 1 : 0,
    handleAxis === "z" ? 1 : 0,
  );

  // For global orientation the unit vector is already in world space;
  // for other orientations, rotate by the orientation quaternion.
  const worldDir =
    orientation === "global"
      ? unitVec
      : unitVec.applyQuaternion(orientationQuat);

  // The command buffer's "normal" axis path expects the vector in object-local
  // space for edit mode, or world space for object mode.
  if (isEditMode) {
    // Transform world direction into object-local space
    const invObjQuat = objectQuat.clone().invert();
    worldDir.applyQuaternion(invObjQuat);
  }

  return {
    axis: "normal",
    normalVector: [worldDir.x, worldDir.y, worldDir.z],
  };
}
